import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/modules/auth'
import { Eye, EyeOff, AlertCircle } from '@/ui/icons'
import { ApiError } from '@/shared/api'
import { Button } from '@/ui/components/'
import { Input } from '@/ui/components/'
import { Logo } from '@/ui/components'
import { MEDIA_CONFIG } from '@/shared/utils'
import { PageMeta } from '@/components/PageMeta'

export default function Login() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(searchParams.get('error') || '')
  const [resetSuccessMessage, setResetSuccessMessage] = useState(
    searchParams.get('reset') === 'success'
      ? 'Your password was updated. Sign in with your new password.'
      : ''
  )
  const [loading, setLoading] = useState(false)

  const { login, loginWithGoogle } = useAuth()

  useEffect(() => {
    const urlError = searchParams.get('error')
    if (urlError && !error) {
      setError(urlError)
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('error')
        return newParams
      }, { replace: true })
    }
  }, [searchParams, setSearchParams, error])

  useEffect(() => {
    if (searchParams.get('reset') !== 'success') return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('reset')
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const redirectTo = searchParams.get('redirect')
      await login(formData, { redirectTo })
      setError('')
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        const errorMsg = err.message && err.message !== 'HTTP 401'
          ? err.message
          : 'Invalid email or password. Please check your credentials and try again.'
        setSearchParams({ error: errorMsg }, { replace: true })
        setError(errorMsg)
        setLoading(false)
        return
      }

      if (err instanceof ApiError && err.status === 0) {
        const errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.'
        setSearchParams({ error: errorMsg }, { replace: true })
        setError(errorMsg)
        setLoading(false)
        return
      }

      if (err instanceof ApiError) {
        const errorMsg = err.message || 'An error occurred during sign in. Please try again.'
        setSearchParams({ error: errorMsg }, { replace: true })
        setError(errorMsg)
        setLoading(false)
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.'
      setSearchParams({ error: errorMessage }, { replace: true })
      setError(errorMessage)
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (resetSuccessMessage) {
      setResetSuccessMessage('')
    }
    if (error || searchParams.get('error')) {
      setError('')
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('error')
        return newParams
      }, { replace: true })
    }
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <>
      <PageMeta title="Log in" description="Sign in to SitRepp.io to plan overlanding trips with your crew" />
      <div className="min-h-screen flex">
      {/* ── Left panel: immersive photo ── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col overflow-hidden">
        {/* Background image */}
        <img
          src={MEDIA_CONFIG.hero.image.src}
          alt="Overlanding convoy on a mountain trail"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Logo */}
        <div className="relative z-10 p-10">
          <Logo to="/" size="sm" className="h-8 brightness-0 invert" />
        </div>

        {/* Brand copy */}
        <div className="relative z-10 mt-auto p-10 pb-14">
          <p className="text-xs font-semibold text-white/60 mb-6">
            Coordinate · Plan · Explore
          </p>
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
            Plan Less.<br />Explore More.
          </h1>
          <p className="mt-4 text-base xl:text-lg text-white/75 max-w-sm leading-relaxed">
            The collaborative workspace built for overlanding crews. Build routes, manage gear, and keep your team aligned.
          </p>

          {/* Social proof strip */}
          <div className="mt-10 flex items-center gap-3">
            <div className="flex -space-x-2">
              {['#5C8DC5', '#736F60', '#AD9E90'].map((color, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-white/30 flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: color }}
                >
                  {['T', 'K', 'R'][i]}
                </div>
              ))}
            </div>
            <p className="text-sm text-white/60">
              Crews already planning their next adventure
            </p>
          </div>
        </div>
      </div>

      {/* ── Right panel: form ── */}
      <div
        className="w-full lg:w-1/2 xl:w-2/5 relative flex flex-col min-h-screen"
        style={{
          backgroundImage: `url(${MEDIA_CONFIG.hero.image.src})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Mobile overlay — hidden on desktop since desktop uses solid bg */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm lg:hidden" />
        {/* Desktop solid background */}
        <div className="absolute inset-0 bg-background hidden lg:block" />

        <div className="relative z-10 flex flex-col min-h-screen px-8 py-10 sm:px-12">
          {/* Mobile logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <Logo to="/" size="sm" className="h-10 brightness-0 invert" />
          </div>

          {/* Form area — vertically centered */}
          <div className="flex-1 flex flex-col justify-center w-full max-w-sm mx-auto">
            {/* Heading */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground lg:text-foreground text-white lg:text-current">
                Welcome back
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground lg:text-muted-foreground text-white/70 lg:text-current">
                Sign in to your SitRepp account
              </p>
            </div>

            {/* Google SSO */}
            <button
              type="button"
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-foreground shadow-sm hover:bg-muted transition-colors lg:border-input lg:bg-background lg:text-foreground border-white/30 bg-white/10 text-white hover:bg-white/20 lg:hover:bg-muted"
            >
              <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border lg:border-border border-white/20" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 text-muted-foreground lg:bg-background bg-transparent lg:text-muted-foreground text-white/50">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Email / password form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {resetSuccessMessage && (
                <div
                  role="status"
                  aria-live="polite"
                  className="bg-success/10 border border-success/25 text-success px-4 py-3 rounded-lg text-sm"
                >
                  {resetSuccessMessage}
                </div>
              )}
              {error && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm flex items-start gap-2"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="flex-1">{error}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground lg:text-foreground text-white lg:text-current">
                  Email address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="h-11 rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="password" className="text-sm font-medium text-foreground lg:text-foreground text-white lg:text-current">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="h-11 rounded-xl pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex justify-end pt-1">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-primary hover:text-primary/80 transition-colors lg:text-primary text-white hover:text-white/90"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-xl font-semibold"
                disabled={loading}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            {/* Footer */}
            <p className="mt-8 text-center text-sm text-muted-foreground lg:text-muted-foreground text-white/60">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-semibold text-primary hover:text-primary/80 transition-colors lg:text-primary text-white hover:text-white/80"
              >
                Create one →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
